import { useDraggable } from "@dnd-kit/core";
import type { ComponentMaterial } from "@shared/types";
import {
  AlignLeft,
  BarChart,
  BarChart3,
  Bot,
  Calendar,
  ChartArea,
  ChartLine,
  Check,
  CheckSquare,
  ChevronsRight,
  CircleDot,
  Copy,
  CreditCard,
  Download,
  Eye,
  FileCode,
  FileText,
  Filter,
  FormInput,
  Gauge,
  Image,
  Layers,
  Layout,
  LayoutGrid,
  List,
  ListCollapse,
  Loader2,
  MousePointer,
  Move,
  PackageOpen,
  PanelLeft,
  PanelsTopLeft,
  PieChart,
  RefreshCw,
  Route,
  Rows3,
  Search,
  SlidersHorizontal,
  Sparkles,
  Square,
  Table2,
  TextCursor,
  ToggleLeft,
  Type,
  Upload,
  Video,
  Wand2,
} from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { componentMaterials } from "@/lib/builder/materials";
import {
  downloadComponentSource,
  fetchRegistryComponent,
  fetchRegistryComponents,
  type RegistryComponent,
  type RegistryComponentSummary,
} from "@/lib/builder/registry-client";
import { cn } from "@/lib/utils";

const iconMap: Record<string, React.ElementType> = {
  AlignLeft,
  BarChart,
  BarChart3,
  Bot,
  Calendar,
  ChartArea,
  ChartLine,
  CheckSquare,
  ChevronsRight,
  CircleDot,
  CreditCard,
  Eye,
  FileCode,
  FileText,
  Filter,
  FormInput,
  Gauge,
  Image,
  Layers,
  Layout,
  LayoutGrid,
  List,
  ListCollapse,
  MousePointer,
  Move,
  PanelLeft,
  PanelsTopLeft,
  PieChart,
  Route,
  Rows3,
  Search,
  SlidersHorizontal,
  Sparkles,
  Square,
  Table2,
  TextCursor,
  ToggleLeft,
  Type,
  Upload,
  Video,
  Wand2,
};

function MaterialItem({ material }: { material: ComponentMaterial }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `material__${material.type}`,
    data: { material },
  });
  const Icon = material.icon ? iconMap[material.icon] || Square : Square;

  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={style}
      className="flex cursor-grab items-center gap-3 rounded-md border bg-card p-3 text-sm shadow-sm transition-colors hover:border-primary hover:text-primary"
      title={material.description}
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span>{material.label}</span>
    </div>
  );
}

export function ComponentPanel() {
  const [query, setQuery] = React.useState("");
  const [tab, setTab] = React.useState<"local" | "registry">("local");
  const term = query.trim().toLowerCase();
  const filtered = React.useMemo(() => {
    if (!term) return componentMaterials;
    return componentMaterials.filter(
      (m) =>
        m.label.toLowerCase().includes(term) ||
        m.type.toLowerCase().includes(term) ||
        (m.description ?? "").toLowerCase().includes(term),
    );
  }, [term]);

  const categories = Array.from(new Set(filtered.map((m) => m.category)));
  const categoryLabels: Record<string, string> = {
    layout: "布局",
    basic: "基础",
    data: "数据",
    ai: "AI",
    chart: "图表",
    content: "内容",
    motion: "动效",
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 rounded-md bg-muted/60 p-1 text-xs">
        <button
          type="button"
          data-testid="registry-tab-local"
          onClick={() => setTab("local")}
          className={cn(
            "flex-1 rounded px-2 py-1 transition-colors",
            tab === "local" ? "bg-background font-medium shadow-sm" : "text-muted-foreground",
          )}
        >
          本地组件
        </button>
        <button
          type="button"
          data-testid="registry-tab-registry"
          onClick={() => setTab("registry")}
          className={cn(
            "flex flex-1 items-center justify-center gap-1 rounded px-2 py-1 transition-colors",
            tab === "registry" ? "bg-background font-medium shadow-sm" : "text-muted-foreground",
          )}
        >
          <PackageOpen className="h-3 w-3" />
          Registry
        </button>
      </div>
      {tab === "local" ? (
        <>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="搜索组件..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 pl-7 text-xs"
            />
          </div>
          {filtered.length === 0 && <p className="text-xs text-muted-foreground">无匹配组件</p>}
          {categories.map((cat) => (
            <div key={cat}>
              <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{categoryLabels[cat] || cat}</h3>
              <div className="space-y-2">
                {filtered
                  .filter((m) => m.category === cat)
                  .map((m) => (
                    <MaterialItem key={m.type} material={m} />
                  ))}
              </div>
            </div>
          ))}
        </>
      ) : (
        <RegistryBrowser />
      )}
    </div>
  );
}

/** Registry 浏览：远程组件列表 + 安装（swizzle 弹出源码复制/下载）。 */
function RegistryBrowser() {
  const [items, setItems] = React.useState<RegistryComponentSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState<RegistryComponent | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    const list = await fetchRegistryComponents();
    setItems(list);
    setLoading(false);
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const openComponent = async (type: string) => {
    const detail = await fetchRegistryComponent(type);
    if (detail) setSelected(detail);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">远程组件（{items.length}）</p>
        <Button variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={() => void load()} title="刷新">
          <RefreshCw className="h-3 w-3" />
        </Button>
      </div>
      {loading && (
        <div className="flex items-center gap-2 py-4 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          加载 Registry...
        </div>
      )}
      {!loading && items.length === 0 && (
        <p className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
          Registry 暂不可用（清单为空）。
        </p>
      )}
      {items.map((item) => (
        <button
          key={item.type}
          type="button"
          data-testid={`registry-item-${item.type}`}
          onClick={() => void openComponent(item.type)}
          className="flex w-full items-center justify-between gap-2 rounded-md border bg-card p-2.5 text-left text-sm shadow-sm transition-colors hover:border-primary"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{item.label}</span>
            <span className="block truncate text-[10px] text-muted-foreground">
              {item.description || item.category}
            </span>
          </span>
          <PackageOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
      ))}
      <SwizzleDialog open={selected !== null} component={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

/** swizzle 弹出：展示组件源码，支持复制 / 下载 .tsx（eject 到项目）。 */
function SwizzleDialog({
  open,
  component,
  onClose,
}: {
  open: boolean;
  component: RegistryComponent | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = React.useState(false);

  const copySource = async () => {
    if (!component?.source) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(component.source);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      }
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageOpen className="h-4 w-4 text-indigo-600" />
            {component?.label ?? "组件源码"}（swizzle）
          </DialogTitle>
          <DialogDescription>
            将组件源码复制或下载到你的项目（eject）。
            {component?.sourceAvailable === false && "当前组件源码暂缺，仅提供元数据。"}
          </DialogDescription>
        </DialogHeader>
        {component?.sourceAvailable && component.source ? (
          <>
            <pre
              className="max-h-72 overflow-auto rounded-md border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed"
              data-testid="registry-source-block"
            >
              {component.source}
            </pre>
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => void copySource()}>
                {copied ? (
                  <>
                    <Check className="mr-1 h-3.5 w-3.5" />
                    已复制
                  </>
                ) : (
                  <>
                    <Copy className="mr-1 h-3.5 w-3.5" />
                    复制源码
                  </>
                )}
              </Button>
              <Button size="sm" onClick={() => downloadComponentSource(component)}>
                <Download className="mr-1 h-3.5 w-3.5" />
                下载 {component.type}.tsx
              </Button>
            </div>
          </>
        ) : (
          <p className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
            该组件源码暂缺（registry_sources 未收录），请期待后续版本。
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

